import { Autocomplete } from "@mui/material";
import { Axios } from "api/axios";
import BootstrapTextField from "Common/Components/InputComponent/BootstrapTextField";
import WarehouseAutocomplete from "Common/Components/InputComponent/WarehouseAutocomplete";
import ActionButton from "components/Buttons/ActionButton";
import CancelBackButton from "components/Buttons/CancelBackButton";
import FormHeader from "components/Form&DetailsHeader/Form&DetailsHeader";
import LoadingSpinner from "components/LoaderErrorWrapper/LoadingSpinner";
import PageContainer from "components/PageContainer/PageContainer";
import SubmitButton from "components/Buttons/SubmitButton";
import Colors from "Constants/Color";
import { autocompleteDropdownStyles } from "Constants/Style";
import { useAutoMinimizeSidebar } from "CustomHooks/useAutoMinimizeSidebar";
import { useFormik } from "formik";
import { api } from "helpers/api_url";
import React, { useEffect, useState } from "react";
import { Col, Form, Row, Spinner } from "react-bootstrap";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import QRScanner from "utils/QRScanner";
import { showConfirm, showError } from "utils/swalHelper";
import * as Yup from "yup";
import QuotationItemTable from "./QuotationItemTable";

interface FormValues {
  quotation_date: string;
  dispatch_from_warehouse: any;
  customer: any;
  items: any;
  remarks: string;
  enquiry_no?: string;
  enquiry_id?: string;
}

interface QuotationFormProps {
  enquiryId?: string;
}

const QuotationForm: React.FC<QuotationFormProps> = ({ enquiryId }) => {
  document.title = "quotation form";
  const { id } = useParams();
  const { state, pathname } = useLocation();
  const navigate = useNavigate();
  const [formLoading, setFormLoading] = useState<boolean>(false);
  const [customerOptions, setCustomerOptions] = useState<any>([]);
  const [dispatchWarehouseOptions, setDispatchWarehouseOptions] = useState<any>(
    []
  );
  const [loadingCustomerOptions, setLoadingCustomerOptions] =
    useState<boolean>(true);
  const [loadingWarehouseOptions, setLoadingWarehouseOptions] =
    useState<boolean>(true);
  const [editingItemId, setEditingItemId] = useState<number | null>(null);
  const [quotationData, setQuotationData] = useState<any>(null);
  const [bagQuantityValidation, setBagQuantityValidation] = useState<
    Array<{
      inventory_item_id: string | number;
      available_quantity: number;
      issue_quantity: number;
    }>
  >([]);
  const [showScanner, setShowScanner] = useState(false);
  const [isScanningItem, setIsScanningItem] = useState<boolean>(false);

  // Auto-minimize sidebar on tablet screens
  useAutoMinimizeSidebar();

  const formik = useFormik<FormValues>({
    enableReinitialize: true,
    initialValues: {
      quotation_date: new Date().toISOString().split("T")[0],
      dispatch_from_warehouse: null,
      customer: null,
      items: [],
      remarks: "",
      enquiry_no: "",
      enquiry_id: enquiryId || "",
    },
    validationSchema: Yup.object({
      quotation_date: Yup.string().required("Quotation date is required"),
      dispatch_from_warehouse: Yup.object()
        .nullable()
        .required("Dispatch from warehouse is required"),
      customer: Yup.object().nullable().required("Customer name is required"),
      items: Yup.array()
        .min(1, "At least one item is required")
        .required("At least one item is required"),
    }),
    onSubmit: async (values) => {
      // Validate items before submission
      if (!values.items || values.items.length === 0) {
        formik.setFieldTouched("items", true);
        toast.error("At least one item is required");
        return;
      }

      // Validate that required quantity doesn't exceed available stock for each item
      for (const item of values.items) {
        const requiredQty = parseFloat(String(item.quantity)) || 0;

        if (!item.bags || !Array.isArray(item.bags) || item.bags.length === 0) {
          toast.error(
            `Item "${
              item.item?.item_name || item.item_name || "Unknown"
            }" must have at least one bag selected`
          );
          formik.setFieldTouched("items", true);
          return;
        }

        // Calculate total issue quantity from all bags
        const totalIssueQuantity = item.bags.reduce((sum: number, bag: any) => {
          return sum + (parseFloat(String(bag.issue_quantity)) || 0);
        }, 0);

        // Validate that total issue quantity equals required quantity
        if (Math.abs(totalIssueQuantity - requiredQty) > 0.01) {
          // Allow small floating point differences
          toast.error(
            `Item "${
              item.item?.item_name || item.item_name || "Unknown"
            }": Total Issue Quantity (${totalIssueQuantity.toFixed(
              2
            )}) must equal Required Quantity (${requiredQty.toFixed(2)})`
          );
          formik.setFieldTouched("items", true);
          return;
        }

        // Calculate total available quantity from all bags
        const totalAvailableQuantity = item.bags.reduce(
          (sum: number, bag: any) => {
            return sum + (parseFloat(String(bag.available_quantity)) || 0);
          },
          0
        );

        // Validate that required quantity doesn't exceed total available stock
        if (requiredQty > totalAvailableQuantity) {
          toast.error(
            `Item "${
              item.item?.item_name || item.item_name || "Unknown"
            }": Required Quantity (${requiredQty.toFixed(
              2
            )}) exceeds Total Available Stock (${totalAvailableQuantity.toFixed(
              2
            )})`
          );
          formik.setFieldTouched("items", true);
          return;
        }

        // Validate that each bag's issue quantity doesn't exceed its available quantity
        for (const bag of item.bags) {
          const issueQty = parseFloat(String(bag.issue_quantity)) || 0;
          const availableQty = parseFloat(String(bag.available_quantity)) || 0;

          if (issueQty > availableQty) {
            toast.error(
              `Item "${
                item.item?.item_name || item.item_name || "Unknown"
              }": Bag "${bag.bag_no}" - Issue Quantity (${issueQty.toFixed(
                2
              )}) exceeds Available Quantity (${availableQty.toFixed(2)})`
            );
            formik.setFieldTouched("items", true);
            return;
          }
        }
      }

      // Calculate financial summary values from items
      const subtotalBeforeDiscount =
        values?.items?.reduce((sum: number, item: any) => {
          return sum + parseFloat(item.amount || 0);
        }, 0) || 0;

      const totalItemDiscounts =
        values?.items?.reduce((sum: number, item: any) => {
          return sum + parseFloat(item.discount_amount || 0);
        }, 0) || 0;

      const netTaxableValue = subtotalBeforeDiscount - totalItemDiscounts;

      const totalGst =
        values?.items?.reduce((sum: number, item: any) => {
          return sum + parseFloat(item.gst_amount || 0);
        }, 0) || 0;

      const totalAmount =
        values?.items?.reduce((sum: number, item: any) => {
          return sum + parseFloat(item.total_amount || 0);
        }, 0) || 0;

      const payload = {
        quotation_date: values?.quotation_date,
        dispatch_from_warehouse_id:
          values?.dispatch_from_warehouse?.warehouse_id,
        customer_id: values?.customer?.customer_id,
        customer_details: {
          ...values?.customer,
        },
        subtotal_before_discount: parseFloat(subtotalBeforeDiscount.toFixed(2)),
        total_item_discounts: parseFloat(totalItemDiscounts.toFixed(2)),
        net_taxable_value: parseFloat(netTaxableValue.toFixed(2)),
        total_gst: parseFloat(totalGst.toFixed(2)),
        total_amount: parseFloat(totalAmount.toFixed(2)),
        remarks: values?.remarks,
        enquiry_no: values?.enquiry_no || null,
        enquiry_id: values?.enquiry_id || null,
        quotation_items: values?.items?.map((item: any) => ({
          category_id: item?.category?.category_id || item?.category_id,
          category_name: item?.category?.category_name || item?.category_name,
          sub_category_id:
            item?.sub_category?.sub_category_id || item?.sub_category_id,
          sub_category_name:
            item?.sub_category?.sub_category_name || item?.sub_category_name,
          item_id: item?.item?.item_id || item?.item_id,
          item_name: item?.item?.item_name || item?.item_name,
          item_code: item?.item?.item_code || item?.item_code,
          item_details: {},
          // HSN Code fields - check both nested and flat structures
          hsn_code_id:
            item?.item?.hsn_code?.hsn_code_id || item?.hsn_code_id || null,
          hsn_code_name:
            item?.item?.hsn_code?.hsn_code || item?.hsn_code_name || null,
          // Unit fields - check both nested and flat structures
          unit_id: item?.item?.unit?.unit_id || item?.unit_id || null,
          unit_name:
            item?.unit_name ||
            item?.unit ||
            item?.item?.unit?.unit_name ||
            null,
          unit_symbol:
            item?.unit_symbol || item?.item?.unit?.unit_symbol || null,
          // Quantity fields
          required_quantity: parseFloat(item?.quantity) || 0,
          quantity: parseFloat(item?.quantity) || 0,
          // Bags
          bags:
            item?.bags && Array.isArray(item.bags)
              ? item.bags.map((bag: any) => ({
                  ...bag,
                  available_quantity:
                    typeof bag.available_quantity === "string"
                      ? parseFloat(bag.available_quantity)
                      : bag.available_quantity,
                  issue_quantity:
                    typeof bag.issue_quantity === "string"
                      ? parseFloat(bag.issue_quantity)
                      : bag.issue_quantity,
                }))
              : item?.bags,
          // Price fields
          selling_price: parseFloat(item?.rate) || 0,
          rate: parseFloat(item?.rate) || 0,
          amount: parseFloat(item?.amount) || 0,
          // Discount fields
          discount: item?.discount ? parseFloat(item?.discount) : 0,
          discount_type:
            item?.discount_type === "%"
              ? "percentage"
              : item?.discount_type === "₹"
              ? "amount"
              : item?.discount_type,
          discount_amount: parseFloat(item?.discount_amount) || 0,
          // GST fields - check both nested and flat structures
          gst_id: item?.gst?.gst_id || item?.gst_id || null,
          gst_percent: item?.gst?.gst_percent
            ? parseFloat(item?.gst?.gst_percent)
            : item?.gst_percent
            ? parseFloat(item?.gst_percent)
            : 0,
          gst_amount: parseFloat(item?.gst_amount) || 0,
          // Total and remarks
          total_amount: parseFloat(item.total_amount) || 0,
          remarks: item?.remarks || null,
        })),
      };

      try {
        if (id) {
          await Axios.put(`/quotation/${id}/update/`, payload);
          toast.success("Quotation Updated Successfully");
          navigate("/quotation");
        } else {
          await Axios.post(api?.quotation?.create, payload);
          toast.success("Quotation Created Successfully");
          navigate("/quotation", { state: { clearState: true } });
        }
      } catch (err: any) {
        const backendMessage =
          err?.response?.data?.error || err?.response?.data?.message;
        if (backendMessage) {
          toast.error(backendMessage);
        } else {
          toast.error("Something went wrong. Please try again.");
        }
      }
    },
  });

  const tableValidationSchema = Yup.object().shape({
    category: Yup.object().nullable().required("Category is required"),
    sub_category: Yup.object().nullable().required("Sub Category is required"),
    item: Yup.object().nullable().required("Item Name is required"),
    item_code: Yup.string().trim().required("Item Code is required"),
    unit: Yup.string().trim().required("Unit is required"),
    quantity: Yup.number()
      .typeError("Quantity must be a number")
      .positive("Quantity must be greater than 0")
      .required("Quantity is required"),
    bags: Yup.array()
      .of(
        Yup.object().shape({
          inventory_item_id: Yup.mixed().required(),
          bag_no: Yup.string().required(),
          available_quantity: Yup.number().required(),
          issue_quantity: Yup.number().required(),
        })
      )
      .min(1, "At least one bag is required")
      .required("At least one bag is required"),
    rate: Yup.number()
      .typeError("Rate must be a number")
      .positive("Rate must be greater than 0")
      .required("Rate is required"),
    amount: Yup.number()
      .typeError("Amount must be a number")
      .required("Amount is required"),
    discount: Yup.number()
      .typeError("Discount must be a number")
      .min(0, "Discount cannot be negative")
      .nullable(),
    discount_type: Yup.string()
      .oneOf(["%", "₹"], "Invalid discount type")
      .required("Discount type is required"),
    discount_amount: Yup.number()
      .typeError("Discount amount must be a number")
      .min(0, "Discount amount cannot be negative")
      .nullable(),
    gst: Yup.object().nullable().required("GST is required"),
    gst_amount: Yup.number()
      .typeError("GST amount must be a number")
      .min(0, "GST amount cannot be negative")
      .required("GST amount is required"),
    total_amount: Yup.number()
      .typeError("Total amount must be a number")
      .min(0, "Total amount cannot be negative")
      .required("Total amount is required"),
    remarks: Yup.string().trim().nullable(),
  });

  const tableFormik = useFormik<any>({
    enableReinitialize: true,
    validateOnChange: false,
    validateOnBlur: false,
    validateOnMount: false,
    initialValues: {
      category: null,
      sub_category: null,
      item: null,
      item_code: "",
      unit: "",
      quantity: "",
      bags: [],
      rate: "",
      amount: "",
      discount: "",
      discount_type: "%",
      discount_amount: "",
      gst: null,
      gst_amount: "",
      total_amount: "",
      remarks: "",
    },
    validationSchema: tableValidationSchema,
    onSubmit: async (values) => {
      const payload = {
        ...values,
      };

      // Validate bags
      if (!values.bags || values.bags.length === 0) {
        tableFormik.setFieldTouched("bags", true);
        toast.error("At least one bag is required");
        return;
      }

      // Calculate total issue quantity from all bags
      const totalIssueQuantity = values.bags.reduce((sum: number, bag: any) => {
        const issueQty = parseFloat(String(bag.issue_quantity)) || 0;
        return sum + issueQty;
      }, 0);

      // Validate quantity field is not less than total issue quantity from bags
      const quantity = parseFloat(String(values.quantity)) || 0;
      if (quantity < totalIssueQuantity) {
        tableFormik.setFieldTouched("quantity", true);
        toast.error(
          `Quantity (${quantity}) cannot be less than Total Issue Quantity (${totalIssueQuantity.toFixed(
            2
          )}) from selected bags`
        );
        return;
      }

      // Validate that quantity equals total issue quantity from bags
      if (Math.abs(quantity - totalIssueQuantity) > 0.01) {
        // Allow small floating point differences
        tableFormik.setFieldTouched("quantity", true);
        toast.error(
          `Quantity (${quantity}) must equal Total Issue Quantity (${totalIssueQuantity.toFixed(
            2
          )}) from selected bags`
        );
        return;
      }

      // Calculate total available quantity from all bags
      const totalAvailableQuantity = values.bags.reduce(
        (sum: number, bag: any) => {
          const availableQty = parseFloat(String(bag.available_quantity)) || 0;
          return sum + availableQty;
        },
        0
      );

      // Validate that required quantity doesn't exceed total available stock
      if (quantity > totalAvailableQuantity) {
        tableFormik.setFieldTouched("quantity", true);
        toast.error(
          `Required Quantity (${quantity.toFixed(
            2
          )}) exceeds Total Available Stock (${totalAvailableQuantity.toFixed(
            2
          )}) from selected bags`
        );
        return;
      }

      const currentItems = formik.values.items || [];

      if (editingItemId !== null) {
        // Update existing item
        const updatedItems = currentItems.map((item: any) =>
          item.id === editingItemId ? { ...payload, id: editingItemId } : item
        );
        formik.setFieldValue("items", updatedItems);
      } else {
        // Add new item
        const newItem = {
          ...payload,
          id: Date.now(), // Temporary ID for the item
        };
        formik.setFieldValue("items", [...currentItems, newItem]);
      }

      // Clear items validation error if it exists
      if (formik.touched.items && formik.errors.items) {
        formik.setFieldError("items", undefined);
      }

      // Reset table formik and editing state after adding/updating item
      tableFormik.resetForm();
      setEditingItemId(null);
    },
  });

  // Custom submit handler that validates first and shows toasts instead of form errors
  const handleTableSubmit = async () => {
    // Clear any existing errors first
    tableFormik.setErrors({});

    // Validate form first
    const errors = await tableFormik.validateForm();

    if (Object.keys(errors).length > 0) {
      // Helper function to extract error message from a field
      const getErrorMessage = (
        field: string,
        errorMessage: any
      ): string | null => {
        if (!errorMessage) return null;

        if (typeof errorMessage === "string") {
          return errorMessage;
        } else if (Array.isArray(errorMessage)) {
          // Handle array errors (like bags)
          for (let index = 0; index < errorMessage.length; index++) {
            const err = errorMessage[index];
            if (typeof err === "string") {
              return `${field} - ${err}`;
            } else if (err && typeof err === "object") {
              // Handle nested object errors in array
              for (const subField of Object.keys(err)) {
                const subError = err[subField];
                if (typeof subError === "string") {
                  return `${field}[${index}].${subField}: ${subError}`;
                }
              }
            }
          }
        } else if (errorMessage && typeof errorMessage === "object") {
          // Handle nested object errors
          for (const subField of Object.keys(errorMessage)) {
            const subError = errorMessage[subField];
            if (typeof subError === "string") {
              return `${field}.${subField}: ${subError}`;
            }
          }
        }
        return `${field} is required`;
      };

      // Priority order: category should be checked first
      const priorityFields = [
        "category",
        "sub_category",
        "item",
        "item_code",
        "unit",
        "quantity",
        "bags",
        "rate",
        "amount",
        "discount",
        "discount_type",
        "discount_amount",
        "gst",
        "gst_amount",
        "total_amount",
        "remark",
      ];

      // Find the first error in priority order
      let firstError: string | null = null;

      for (const field of priorityFields) {
        if (errors[field]) {
          firstError = getErrorMessage(field, errors[field]);
          if (firstError) break;
        }
      }

      // If no error found in priority fields, check other fields
      if (!firstError) {
        for (const field of Object.keys(errors)) {
          if (!priorityFields.includes(field)) {
            firstError = getErrorMessage(field, errors[field]);
            if (firstError) break;
          }
        }
      }

      // Show only the first error message as a single toast
      if (firstError) {
        toast.warning(firstError);
      }

      // Ensure errors are cleared so they don't show in the form fields
      tableFormik.setErrors({});
      return;
    }

    // If validation passes, directly execute the onSubmit logic
    // This avoids formik's handleSubmit which would validate again and set errors
    const values = tableFormik.values;
    const payload = {
      ...values,
    };

    // Validate bags
    if (!values.bags || values.bags.length === 0) {
      toast.warning("At least one bag is required");
      return;
    }

    // Calculate total issue quantity from all bags
    const totalIssueQuantity = values.bags.reduce((sum: number, bag: any) => {
      const issueQty = parseFloat(String(bag.issue_quantity)) || 0;
      return sum + issueQty;
    }, 0);

    // Validate quantity field is not less than total issue quantity from bags
    const quantity = parseFloat(String(values.quantity)) || 0;
    if (quantity < totalIssueQuantity) {
      toast.warning(
        `Quantity (${quantity}) cannot be less than Total Issue Quantity (${totalIssueQuantity.toFixed(
          2
        )}) from selected bags`
      );
      return;
    }

    // Validate that quantity equals total issue quantity from bags
    if (Math.abs(quantity - totalIssueQuantity) > 0.01) {
      // Allow small floating point differences
      toast.warning(
        `Quantity (${quantity}) must equal Total Issue Quantity (${totalIssueQuantity.toFixed(
          2
        )}) from selected bags`
      );
      return;
    }

    // Calculate total available quantity from all bags
    const totalAvailableQuantity = values.bags.reduce(
      (sum: number, bag: any) => {
        const availableQty = parseFloat(String(bag.available_quantity)) || 0;
        return sum + availableQty;
      },
      0
    );

    // Validate that required quantity doesn't exceed total available stock
    if (quantity > totalAvailableQuantity) {
      toast.warning(
        `Required Quantity (${quantity.toFixed(
          2
        )}) exceeds Total Available Stock (${totalAvailableQuantity.toFixed(
          2
        )}) from selected bags`
      );
      return;
    }

    const currentItems = formik.values.items || [];

    // Recalculate total_amount to ensure it's accurate
    const amount = parseFloat(String(payload.amount)) || 0;
    const discountAmount = parseFloat(String(payload.discount_amount)) || 0;
    const gstAmount = parseFloat(String(payload.gst_amount)) || 0;
    const netAmount = amount - discountAmount;
    const totalAmount = netAmount + gstAmount;
    const itemWithCalculatedTotal = {
      ...payload,
      total_amount: isNaN(totalAmount) ? "0.00" : totalAmount.toFixed(2),
    };

    if (editingItemId !== null) {
      // Update existing item
      const updatedItems = currentItems.map((item: any) =>
        item.id === editingItemId
          ? { ...itemWithCalculatedTotal, id: editingItemId }
          : item
      );
      formik.setFieldValue("items", updatedItems);
    } else {
      // Add new item
      const newItem = {
        ...itemWithCalculatedTotal,
        id: Date.now(), // Temporary ID for the item
      };
      formik.setFieldValue("items", [...currentItems, newItem]);
    }

    // Clear items validation error if it exists
    if (formik.touched.items && formik.errors.items) {
      formik.setFieldError("items", undefined);
    }

    // Reset table formik and editing state after adding/updating item
    tableFormik.resetForm();
    setEditingItemId(null);
  };

  useEffect(() => {
    if (id) {
      setFormLoading(true);
      Axios.get(`${api?.quotation?.dropdown}/${id}`)
        .then((result: any) => {
          const data = result?.data?.data;
          setQuotationData(data);

          formik.setFieldValue(
            "quotation_date",
            data?.quotation_date?.split("T")?.[0]
          );
          formik.setFieldValue("remarks", data?.remarks || "");
          formik.setFieldValue("enquiry_no", data?.enquiry_no || "");
          formik.setFieldValue("enquiry_id", data?.enquiry_id || "");

          const quotationItems = data?.quotation_items || [];
          // Normalize items to match form structure (both flat and nested structures)
          const itemsWithId = quotationItems.map((item: any, idx: number) => {
            // Preserve all original fields (flat structure from API)
            const normalizedItem = {
              ...item,
              discount_type: item.discount_type === "percentage" ? "%" : "₹",
              id:
                item.id ||
                item.quotation_item_id ||
                `item-${Date.now()}-${idx}`,
            };

            // Build nested structure for form compatibility if flat fields exist
            // This ensures the form can access both item.item and item.hsn_code_id
            if (item.item_id && !normalizedItem.item) {
              normalizedItem.item = {
                item_id: item.item_id,
                item_name: item.item_name,
                item_code: item.item_code,
                hsn_code: item.hsn_code_id
                  ? {
                      hsn_code_id: item.hsn_code_id,
                      hsn_code: item.hsn_code_name,
                    }
                  : undefined,
                unit: item.unit_id
                  ? {
                      unit_id: item.unit_id,
                      unit_name: item.unit_name,
                      unit_symbol: item.unit_symbol,
                    }
                  : undefined,
              };
            }

            if (item.category_id && !normalizedItem.category) {
              normalizedItem.category = {
                category_id: item.category_id,
                category_name: item.category_name,
              };
            }

            if (item.sub_category_id && !normalizedItem.sub_category) {
              normalizedItem.sub_category = {
                sub_category_id: item.sub_category_id,
                sub_category_name: item.sub_category_name,
              };
            }

            if (item.gst_id && !normalizedItem.gst) {
              normalizedItem.gst = {
                gst_id: item.gst_id,
                gst_percent: item.gst_percent,
              };
            }

            // Ensure unit field is set (can be string or object)
            if (!normalizedItem.unit && item.unit_name) {
              normalizedItem.unit = item.unit_name;
            }

            // remarks
            if (item.remarks && !normalizedItem.remarks) {
              normalizedItem.remarks = item.remarks;
            }

            // Calculate discount_amount on frontend since it's not stored in database
            // This ensures discount_amount is always calculated correctly on initial render
            const amount = parseFloat(String(normalizedItem.amount || 0)) || 0;
            const discount =
              parseFloat(String(normalizedItem.discount || 0)) || 0;
            const discountType = normalizedItem.discount_type || "%";

            // Calculate discount_amount based on discount_type
            let calculatedDiscountAmount = 0;
            if (discount > 0 && amount > 0) {
              if (discountType === "%") {
                calculatedDiscountAmount = (amount * discount) / 100;
              } else if (discountType === "₹") {
                calculatedDiscountAmount = discount;
              }
            }

            // Always set the calculated discount_amount (since it's not stored in DB)
            normalizedItem.discount_amount =
              calculatedDiscountAmount.toFixed(2);

            // Recalculate GST amount and total amount after discount_amount calculation
            // This ensures all financial values are correct on initial load
            const discountAmount =
              parseFloat(String(normalizedItem.discount_amount)) || 0;
            const netAmount = amount - discountAmount;
            const gstPercent = normalizedItem.gst?.gst_percent
              ? parseFloat(String(normalizedItem.gst.gst_percent))
              : normalizedItem.gst_percent
              ? parseFloat(String(normalizedItem.gst_percent))
              : 0;

            const gstAmount = (netAmount * gstPercent) / 100;
            const totalAmount = netAmount + gstAmount;

            normalizedItem.gst_amount = gstAmount.toFixed(2);
            normalizedItem.total_amount = totalAmount.toFixed(2);

            return normalizedItem;
          });
          formik.setFieldValue("items", itemsWithId);

          // Initialize bagQuantityValidation with existing bags from all items
          const initialBagValidation: Array<{
            inventory_item_id: string | number;
            available_quantity: number;
            issue_quantity: number;
          }> = [];

          itemsWithId.forEach((item: any) => {
            if (item.bags && Array.isArray(item.bags)) {
              item.bags.forEach((bag: any) => {
                const inventoryItemId = bag.inventory_item_id;
                const issueQty = parseFloat(String(bag.issue_quantity)) || 0;
                const availableQty =
                  parseFloat(String(bag.available_quantity)) || 0;

                const existingBagIndex = initialBagValidation.findIndex(
                  (b) => String(b.inventory_item_id) === String(inventoryItemId)
                );

                if (existingBagIndex !== -1) {
                  // Bag exists, append issue quantity
                  initialBagValidation[existingBagIndex].issue_quantity +=
                    issueQty;
                } else {
                  // Bag doesn't exist, add new entry
                  initialBagValidation.push({
                    inventory_item_id: inventoryItemId,
                    available_quantity: availableQty,
                    issue_quantity: issueQty,
                  });
                }
              });
            }
          });

          setBagQuantityValidation(initialBagValidation);
        })
        .catch((err) => {
          toast.error("Failed to fetch quotation data");
        })
        .finally(() => {
          setFormLoading(false);
        });
    }
  }, [id]);

  useEffect(() => {
    setLoadingCustomerOptions(true);
    setLoadingWarehouseOptions(true);

    Axios.get(`${api?.master?.customer?.dropdown}`)
      .then((result) => {
        setCustomerOptions(result?.data?.data || []);
      })
      .catch((err) => {
        toast.error("Failed to fetch customer dropdown");
      })
      .finally(() => {
        setLoadingCustomerOptions(false);
      });

    Axios.get(`${api?.master?.warehouse?.dropdown_all}`)
      .then((result) => {
        setDispatchWarehouseOptions(result?.data?.data || []);
      })
      .catch((err) => {
        toast.error("Failed to fetch warehouse dropdown");
      })
      .finally(() => {
        setLoadingWarehouseOptions(false);
      });
  }, []);

  // Set warehouse and customer values once dropdowns are loaded
  useEffect(() => {
    if (
      quotationData &&
      dispatchWarehouseOptions.length > 0 &&
      customerOptions.length > 0
    ) {
      // Map warehouse - find from dropdown options using dispatch_from_warehouse_id
      if (quotationData?.dispatch_from_warehouse_id) {
        const warehouse = dispatchWarehouseOptions.find(
          (w: any) =>
            w.warehouse_id === quotationData.dispatch_from_warehouse_id
        );
        if (warehouse) {
          formik.setFieldValue("dispatch_from_warehouse", warehouse);
        }
      }

      // Map customer - find from dropdown options using customer_id from customer_details
      if (quotationData?.customer_details?.customer_id) {
        const customer = customerOptions.find(
          (c: any) =>
            c.customer_id === quotationData.customer_details.customer_id
        );
        if (customer) {
          formik.setFieldValue("customer", customer);
        }
      }
    }
  }, [quotationData, dispatchWarehouseOptions, customerOptions]);

  // Handle QR scan result
  const handleQRScanResult = async (scannedValue: any) => {
    // Validate that dispatch from warehouse is selected
    if (
      !formik.values.dispatch_from_warehouse ||
      !formik.values.dispatch_from_warehouse?.warehouse_id
    ) {
      toast.error(
        "Please select Dispatch From warehouse before scanning items"
      );
      setShowScanner(false);
      return;
    }

    let inventoryItemId: string | null = null;

    // Extract inventory_item_id from scanned value
    if (typeof scannedValue === "string") {
      inventoryItemId = scannedValue;
    } else if (typeof scannedValue === "object" && scannedValue !== null) {
      inventoryItemId =
        scannedValue.inventory_item_id || scannedValue.id || scannedValue;
    }

    if (!inventoryItemId) {
      toast.error("Invalid QR code. Could not extract inventory item ID.");
      return;
    }

    try {
      setIsScanningItem(true);
      // Get warehouse_id to send as query parameter
      const warehouseId = formik.values.dispatch_from_warehouse?.warehouse_id;

      const response = await Axios.get(
        `${api.warehouse.dispatch.stock_transfer.inventory_item_by_id}${inventoryItemId}`,
        {
          params: {
            warehouse_id: warehouseId,
          },
        }
      );

      if (response.data?.success && response.data?.data) {
        const itemData = response.data.data;

        // Check if item already exists in the list (by inventory_item_id in bags)
        const existingItem = formik.values.items?.find((item: any) => {
          if (item.bags && Array.isArray(item.bags)) {
            return item.bags.some(
              (bag: any) => bag.inventory_item_id === inventoryItemId
            );
          }
          return false;
        });

        if (existingItem) {
          toast.warning("Item already added to the list");
          setIsScanningItem(false);
          return;
        }

        // Validate available quantity is greater than 0
        const availableQty = parseFloat(String(itemData.available_quantity)) || 0;
        if (availableQty <= 0) {
          toast.error(
            "Bag cannot be added. Available quantity is zero or invalid."
          );
          setIsScanningItem(false);
          return;
        }

        // Create bag data
        const bagData = {
          bag_no: itemData.bag_no || "",
          inventory_item_id: itemData.inventory_item_id,
          available_quantity: itemData.available_quantity,
          issue_quantity: itemData.available_quantity, // Default to available quantity
        };

        // Validate required item fields
        if (!itemData.item?.item_id || !itemData.item?.item_code) {
          toast.error(
            "Item data is incomplete. Missing item ID or item code."
          );
          setIsScanningItem(false);
          return;
        }

        if (!itemData.item?.category_id || !itemData.item?.sub_category_id) {
          toast.error(
            "Item category or sub-category information is missing. Cannot add item."
          );
          setIsScanningItem(false);
          return;
        }

        // Get rate from master_item selling_price (from itemData.item.selling_price)
        const rate = parseFloat(String(itemData.item?.selling_price || 0));

        // Get GST percent from master_item hsn_code
        const gstPercent =
          itemData.item?.hsn_code?.gst_percent ||
          itemData.item?.gst_percent ||
          0;

        // Calculate amounts
        const quantity = itemData.available_quantity;
        const amount = parseFloat((quantity * rate).toFixed(2));
        const discount = 0;
        const discountType = "%";
        const discountAmount = 0;
        const netAmount = amount - discountAmount;
        const gstAmount = parseFloat(
          ((netAmount * parseFloat(String(gstPercent))) / 100).toFixed(2)
        );
        const totalAmount = parseFloat((netAmount + gstAmount).toFixed(2));

        // Create new quotation item from scanned inventory item
        const newItem = {
          id: Date.now(), // Temporary ID for the item
          category: {
            category_id: itemData.item.category_id,
            category_name: itemData.item.category_name || "",
          },
          sub_category: {
            sub_category_id: itemData.item.sub_category_id,
            sub_category_name: itemData.item.sub_category_name || "",
          },
          item: {
            item_id: itemData.item.item_id,
            item_name: itemData.item.item_name || "",
            item_code: itemData.item.item_code || "",
            unit: itemData.item.unit || {},
            selling_price: rate,
            hsn_code: itemData.item?.hsn_code || null,
          },
          item_code: itemData.item.item_code || "",
          unit: itemData.item?.unit_name || "",
          quantity: quantity,
          bags: [bagData],
          rate: rate,
          amount: amount,
          discount: discount,
          discount_type: discountType,
          discount_amount: discountAmount,
          gst: {
            gst_percent: parseFloat(String(gstPercent)),
          },
          gst_amount: gstAmount,
          total_amount: totalAmount,
          remark: "",
        };

        // Add item directly to the items list
        const currentItems = formik.values.items || [];
        formik.setFieldValue("items", [...currentItems, newItem]);

        // Update bag quantity validation
        if (setBagQuantityValidation) {
          const existingBagIndex = bagQuantityValidation.findIndex(
            (b) => String(b.inventory_item_id) === String(inventoryItemId)
          );

          if (existingBagIndex !== -1) {
            // Bag exists, append issue quantity
            const updatedBags = [...bagQuantityValidation];
            updatedBags[existingBagIndex].issue_quantity += itemData.available_quantity;
            setBagQuantityValidation(updatedBags);
          } else {
            // Bag doesn't exist, add new entry
            setBagQuantityValidation([
              ...bagQuantityValidation,
              {
                inventory_item_id: inventoryItemId,
                available_quantity: itemData.available_quantity,
                issue_quantity: itemData.available_quantity,
              },
            ]);
          }
        }

        // Clear items validation error if it exists
        if (formik.touched.items && formik.errors.items) {
          formik.setFieldError("items", undefined);
        }

        // Clear any editing state when adding from scan
        setEditingItemId(null);

        toast.success("Item added successfully from QR scan");
        setShowScanner(false);
      } else {
        throw new Error("Failed to fetch inventory item");
      }
    } catch (error: any) {
      console.error("Error fetching inventory item:", error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to fetch inventory item. Please try again.";

      // Check if error is about warehouse mismatch
      if (errorMessage.includes("not present in the selected warehouse")) {
        showError(errorMessage, "Warehouse Mismatch");
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setIsScanningItem(false);
    }
  };

  return (
    <PageContainer>
      <FormHeader
        title={`${id ? "Edit" : "Create"} Quotation`}
        firstLabel="Quotation"
        secondLabel={`${id ? "Edit" : "Create"} Quotation`}
        handleArrowClick={() => navigate("/quotation")}
        handleBreadcrumbClick={() => navigate("/quotation")}
      />

      <hr />

      {loadingCustomerOptions || loadingWarehouseOptions ? (
        <LoadingSpinner label="Loading form..." />
      ) : formLoading ? (
        <LoadingSpinner />
      ) : (
        <Form autoComplete="off" onSubmit={formik.handleSubmit} noValidate>
          <Row>
            <Col lg={3} className="mb-3">
              <Form.Label>
                Quotation Date<span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="date"
                name="quotation_date"
                value={formik.values.quotation_date}
                onChange={(e) => {
                  const value = e.target.value;
                  formik.setFieldValue("quotation_date", value);
                }}
                onBlur={formik.handleBlur}
                isInvalid={
                  !!formik.errors.quotation_date &&
                  formik.touched.quotation_date
                }
              />
              <Form.Control.Feedback type="invalid">
                {formik.errors.quotation_date}
              </Form.Control.Feedback>
            </Col>
            <Col lg={3} className="mb-3">
              <Form.Label>
                Dispatch From Warehouse<span className="text-danger">*</span>
              </Form.Label>
              <WarehouseAutocomplete
                size="small"
                options={dispatchWarehouseOptions || []}
                showWarehouseType={true}
                value={formik.values.dispatch_from_warehouse?.warehouse_id || null}
                onChange={async (warehouseId, warehouseName, fullOption) => {
                  const currentWarehouse =
                    formik.values.dispatch_from_warehouse;
                  const currentWarehouseId = currentWarehouse?.warehouse_id;
                  const newWarehouseId = warehouseId || null;

                  // Handle clear case (when warehouse is cleared)
                  if (!warehouseId || !fullOption) {
                    // Check if warehouse is being cleared and items exist
                    if (
                      formik.values.items &&
                      formik.values.items.length > 0 &&
                      currentWarehouseId
                    ) {
                      const result = await showConfirm(
                        "If you change the warehouse, all added items will be removed. Do you want to continue?",
                        "Change Warehouse?",
                        "Yes, Change Warehouse",
                        "Cancel"
                      );

                      if (result?.isConfirmed) {
                        // Clear all items
                        formik.setFieldValue("items", []);
                        // Also clear bag quantity validation
                        setBagQuantityValidation([]);
                        // Reset table form and editing state
                        tableFormik.resetForm();
                        setEditingItemId(null);
                        // Clear warehouse
                        formik.setFieldValue("dispatch_from_warehouse", null);
                      } else {
                        // Revert to previous selection
                        setTimeout(() => {
                          formik.setFieldValue(
                            "dispatch_from_warehouse",
                            currentWarehouse
                          );
                        }, 0);
                      }
                    } else {
                      // No items - just clear warehouse
                      formik.setFieldValue("dispatch_from_warehouse", null);
                    }
                    return;
                  }

                  // Check if warehouse is being changed and items exist
                  if (
                    formik.values.items &&
                    formik.values.items.length > 0 &&
                    currentWarehouseId &&
                    currentWarehouseId !== newWarehouseId
                  ) {
                    const result = await showConfirm(
                      "If you change the warehouse, all added items will be removed. Do you want to continue?",
                      "Change Warehouse?",
                      "Yes, Change Warehouse",
                      "Cancel"
                    );

                    if (result?.isConfirmed) {
                      // Clear all items
                      formik.setFieldValue("items", []);
                      // Also clear bag quantity validation
                      setBagQuantityValidation([]);
                      // Reset table form and editing state
                      tableFormik.resetForm();
                      setEditingItemId(null);
                      // Update warehouse with full option object
                      formik.setFieldValue("dispatch_from_warehouse", fullOption);
                    } else {
                      // Revert to previous selection
                      setTimeout(() => {
                        formik.setFieldValue(
                          "dispatch_from_warehouse",
                          currentWarehouse
                        );
                      }, 0);
                      return;
                    }
                  } else {
                    // No items or first time selection - just update with full option object
                    formik.setFieldValue("dispatch_from_warehouse", fullOption);
                    // Reset table form and editing state when warehouse changes
                    tableFormik.resetForm();
                    setEditingItemId(null);
                    // Clear bag quantity validation
                    setBagQuantityValidation([]);
                  }
                }}
                onBlur={() =>
                  formik.setFieldTouched("dispatch_from_warehouse", true)
                }
                placeholder="Select Warehouse"
                required
                formik={formik}
                name="dispatch_from_warehouse"
                valueType="warehouse_id"
                customSx={{
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": {
                      borderColor:
                        formik.touched.dispatch_from_warehouse &&
                        formik.errors.dispatch_from_warehouse
                          ? "#dc3545"
                          : "#ced4da",
                    },
                    "&:hover fieldset": {
                      borderColor:
                        formik.touched.dispatch_from_warehouse &&
                        formik.errors.dispatch_from_warehouse
                          ? "#dc3545"
                          : "#ced4da",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor:
                        formik.touched.dispatch_from_warehouse &&
                        formik.errors.dispatch_from_warehouse
                          ? "#dc3545"
                          : "#ced4da",
                    },
                  },
                }}
                inputSx={{
                  background: "#FFF",
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": {
                      borderColor:
                        formik.touched.dispatch_from_warehouse &&
                        formik.errors.dispatch_from_warehouse
                          ? "#dc3545"
                          : "#ced4da",
                    },
                    "&:hover fieldset": {
                      borderColor:
                        formik.touched.dispatch_from_warehouse &&
                        formik.errors.dispatch_from_warehouse
                          ? "#dc3545"
                          : "#ced4da",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor:
                        formik.touched.dispatch_from_warehouse &&
                        formik.errors.dispatch_from_warehouse
                          ? "#dc3545"
                          : "#ced4da",
                    },
                  },
                }}
              />
            </Col>
            <Col lg={3} className="mb-3">
              <Form.Label>
                Customer Name<span className="text-danger">*</span>
              </Form.Label>
              <Autocomplete
                size="small"
                options={customerOptions || []}
                autoHighlight
                value={formik.values.customer}
                onChange={(_, val) => formik.setFieldValue("customer", val)}
                onBlur={() => formik.setFieldTouched("customer", true)}
                getOptionLabel={(option: any) => {
                  if (!option) return "";
                  const customerName = option?.customer_name || "";
                  const mobile = option?.contact_number || "";
                  return mobile ? `${customerName} (${mobile})` : customerName;
                }}
                renderOption={(props, option: any) =>
                  autocompleteDropdownStyles.renderOption(
                    props,
                    option,
                    (opt: any) => {
                      const customerName = opt?.customer_name || "";
                      const mobile = opt?.contact_number || "";
                      return mobile
                        ? `${customerName} (${mobile})`
                        : customerName;
                    }
                  )
                }
                ListboxProps={autocompleteDropdownStyles.ListboxProps}
                renderInput={(params) => (
                  <BootstrapTextField
                    {...params}
                    placeholder="Select Customer"
                    fullWidth
                    error={
                      formik.touched.customer && Boolean(formik.errors.customer)
                    }
                    helperText={
                      formik.touched.customer &&
                      typeof formik.errors.customer === "string"
                        ? formik.errors.customer
                        : undefined
                    }
                    sx={{
                      background: "#FFF",
                      ...autocompleteDropdownStyles.inputSx,
                      "& .MuiOutlinedInput-root": {
                        "& fieldset": {
                          borderColor:
                            formik.touched.customer && formik.errors.customer
                              ? "#dc3545"
                              : "#ced4da",
                        },
                        "&:hover fieldset": {
                          borderColor:
                            formik.touched.customer && formik.errors.customer
                              ? "#dc3545"
                              : "#ced4da",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor:
                            formik.touched.customer && formik.errors.customer
                              ? "#dc3545"
                              : "#ced4da",
                        },
                      },
                    }}
                  />
                )}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": {
                      borderColor:
                        formik.touched.customer && formik.errors.customer
                          ? "#dc3545"
                          : "#ced4da",
                    },
                    "&:hover fieldset": {
                      borderColor:
                        formik.touched.customer && formik.errors.customer
                          ? "#dc3545"
                          : "#ced4da",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor:
                        formik.touched.customer && formik.errors.customer
                          ? "#dc3545"
                          : "#ced4da",
                    },
                  },
                }}
              />
            </Col>
            {enquiryId && (
              <Col lg={3} className="mb-3">
                <Form.Label>Enquiry No.</Form.Label>
                <Form.Control
                  type="text"
                  name="enquiry_no"
                  value={formik.values.enquiry_no || ""}
                  disabled
                  placeholder="Auto Fill"
                />
              </Col>
            )}
          </Row>

          {/* Item Details Section */}
          <div className="mb-3">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 className="card-title mb-0">Item Details</h6>
              <ActionButton
                label="Scan"
                icon="bi bi-qr-code-scan"
                handleClick={() => {
                  if (
                    !formik.values.dispatch_from_warehouse ||
                    !formik.values.dispatch_from_warehouse?.warehouse_id
                  ) {
                    toast.error(
                      "Please select Dispatch From warehouse before scanning items"
                    );
                    return;
                  }
                  setShowScanner(true);
                }}
                variant="primary"
                disabled={
                  !formik.values.dispatch_from_warehouse ||
                  !formik.values.dispatch_from_warehouse?.warehouse_id
                }
              />
            </div>
            {/* Dispatch From Warehouse is not selected */}
            {(!formik.values.dispatch_from_warehouse ||
              !formik.values.dispatch_from_warehouse?.warehouse_id) && (
              <div className="alert alert-warning mb-3" role="alert">
                <i className="bi bi-exclamation-triangle me-2"></i>
                Please select Dispatch From warehouse before scanning/adding
                items
              </div>
            )}
            {/* Loading indicator when scanning item */}
            {isScanningItem && (
              <div className="text-center py-3 mb-3">
                <Spinner animation="border" size="sm" className="me-2" />
                <span>Loading item from QR scan...</span>
              </div>
            )}
            <QuotationItemTable
              formik={tableFormik}
              mainFormik={formik}
              editingItemId={editingItemId}
              setEditingItemId={setEditingItemId}
              bagQuantityValidation={bagQuantityValidation}
              setBagQuantityValidation={setBagQuantityValidation}
              warehouseId={formik.values.dispatch_from_warehouse?.warehouse_id}
              customSubmitHandler={handleTableSubmit}
            />
          </div>
          {formik.touched.items && formik.errors.items && (
            <div
              className="text-danger mb-3"
              style={{ fontSize: "0.875rem", marginTop: "-15px" }}
            >
              {typeof formik.errors.items === "string"
                ? formik.errors.items
                : "At least one item is required"}
            </div>
          )}

          {/* Remarks and Financial Summary Section */}
          <Row className="mb-4">
            <Col lg={7} className="mb-3">
              <div
                style={{
                  border: `1px solid ${Colors?.background_color}`,
                  borderRadius: "4px",
                }}
              >
                <div
                  className="d-flex align-items-center gap-2 mb-0 py-2 px-1"
                  style={{ background: Colors?.background_color }}
                >
                  <i
                    className="bi bi-chevron-down"
                    style={{ fontSize: "0.675rem", color: "#000" }}
                  ></i>
                  <h6 className="mb-0">Remarks</h6>
                </div>
                <Form.Control
                  as="textarea"
                  rows={4}
                  name="remarks"
                  placeholder="Enter your remarks"
                  value={formik.values.remarks}
                  onChange={(e) =>
                    formik.setFieldValue("remarks", e.target.value)
                  }
                  onBlur={formik.handleBlur}
                  style={{
                    border: "1px solid #transparant",
                    borderRadius: "0px",
                    resize: "vertical",
                  }}
                />
              </div>
            </Col>
            <Col lg={5} className="mb-3">
              <div
                style={{
                  backgroundColor: Colors?.background_color,
                  padding: "20px",
                  borderRadius: "8px",
                  border: `1px solid ${Colors?.background_color}`,
                }}
              >
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span style={{ fontSize: "0.875rem", color: "#212529" }}>
                    Subtotal (Before Discount):
                  </span>
                  <span
                    style={{
                      fontSize: "0.875rem",
                      fontWeight: "500",
                      color: "#212529",
                    }}
                  >
                    ₹{" "}
                    {formik.values.items
                      ?.reduce((sum: number, item: any) => {
                        return sum + parseFloat(item.amount || 0);
                      }, 0)
                      .toFixed(2)}
                  </span>
                </div>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span style={{ fontSize: "0.875rem", color: "#212529" }}>
                    Total Item Discounts:
                  </span>
                  <span
                    style={{
                      fontSize: "0.875rem",
                      fontWeight: "500",
                      color: "#212529",
                    }}
                  >
                    ₹{" "}
                    {formik.values.items
                      ?.reduce((sum: number, item: any) => {
                        return sum + parseFloat(item.discount_amount || 0);
                      }, 0)
                      .toFixed(2)}
                  </span>
                </div>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span style={{ fontSize: "0.875rem", color: "#212529" }}>
                    Net Taxable Value:
                  </span>
                  <span
                    style={{
                      fontSize: "0.875rem",
                      fontWeight: "500",
                      color: "#212529",
                    }}
                  >
                    ₹{" "}
                    {(
                      formik.values.items?.reduce((sum: number, item: any) => {
                        return sum + parseFloat(item.amount || 0);
                      }, 0) -
                      formik.values.items?.reduce((sum: number, item: any) => {
                        return sum + parseFloat(item.discount_amount || 0);
                      }, 0)
                    ).toFixed(2)}
                  </span>
                </div>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span style={{ fontSize: "0.875rem", color: "#212529" }}>
                    Total GST:
                  </span>
                  <span
                    style={{
                      fontSize: "0.875rem",
                      fontWeight: "500",
                      color: "#212529",
                    }}
                  >
                    ₹{" "}
                    {formik.values.items
                      ?.reduce((sum: number, item: any) => {
                        return sum + parseFloat(item.gst_amount || 0);
                      }, 0)
                      .toFixed(2)}
                  </span>
                </div>
                <hr style={{ margin: "12px 0", borderColor: "#90caf9" }} />
                <div className="d-flex justify-content-between align-items-center">
                  <span
                    style={{
                      fontSize: "1rem",
                      fontWeight: "600",
                      color: "#212529",
                    }}
                  >
                    Total Amount:
                  </span>
                  <span
                    style={{
                      fontSize: "1rem",
                      fontWeight: "700",
                      color: "#212529",
                    }}
                  >
                    ₹{" "}
                    {formik.values.items
                      ?.reduce((sum: number, item: any) => {
                        return sum + parseFloat(item.total_amount || 0);
                      }, 0)
                      .toFixed(2)}
                  </span>
                </div>
              </div>
            </Col>
          </Row>

          <div className="d-flex justify-content-center gap-3 py-3">
            <CancelBackButton
              label="Cancel"
              onClick={() => {
                showConfirm(
                  "Any unsaved changes will be lost.",
                  "Are you sure?",
                  "Yes",
                  "No"
                ).then((result) => {
                  if (result.isConfirmed) {
                    navigate("/quotation");
                  }
                });
              }}
              disabled={formik.isSubmitting}
            />
            <SubmitButton isSubmitting={formik.isSubmitting} isEdit={!!id} />
          </div>
        </Form>
      )}
      <QRScanner
        show={showScanner}
        handleClose={() => setShowScanner(false)}
        onResult={handleQRScanResult}
      />
    </PageContainer>
  );
};

export default QuotationForm;
